import React, {useEffect, useMemo, useState} from 'react';
import {Box, Typography, IconButton} from '@mui/material';
import {t} from 'i18next';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import {ISelectItem} from '../../../../shared/types';
import ArrTreeView from '../../../../components/common/arr-tree-view/ArrTreeView';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrTooltipWrapper from '../../../../components/common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrCheckBox from '../../../common/arr-checkbox/ArrCheckbox';
import {ArrToggle} from '../../../common/arr-toggle/ArrToggle';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {Trans} from 'react-i18next';
import {AssignState, BulkAssignActionOption} from '../bulk-assign/BulkAssign';

// Types Definitions
export interface AssignmentPanelProps {
  treeData: TreeViewBaseItem<ExtendedTreeItemProps>[];
  unAssignTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  assignmentType: 'existing' | 'new';
  actionType: BulkAssignActionOption;
  onTreeSelect?: (values: string[] | null) => void;
  onError?: (errorMessage: string | null) => void;
  startingCount?: number;
  isStartingCountLoading?: boolean;
  showDocumentCount?: number;
  setShowDocumentCount?: (value: number) => void;
  selectedTreeItems?: string[];
  assignmentData?: AssignState | null;
  onAssignmentDataChange?: (assignState: AssignState) => void;
}
export enum BulkAssignSamplingOption {
  Count = 'count',
  Percent = 'percent',
  ParentLevelDocsOnly = 'parent_level_docs_only',
  InclusiveEmail = 'inclusive_email',
}

const enBulkAssign = 'actionMenu.bulkAssignFlyout';

// Expand all nodes on initial load
const getAllIds = (
  nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
): string[] => {
  const result: string[] = [];
  const dfs = (n: TreeViewBaseItem<ExtendedTreeItemProps>) => {
    result.push(n.id);
    n.children?.forEach(dfs);
  };
  nodes.forEach(dfs);
  return result;
};

const TOTAL_PERCENT = 100;
const TREE_CONTENT_HEIGHT = 36;

// ----------------------------------
// ExistingAssignment Component
// ----------------------------------
const AssignmentPanel: React.FC<AssignmentPanelProps> = ({
  treeData,
  unAssignTreeData,
  onTreeSelect,
  onError,
  assignmentType = 'existing',
  actionType,
  startingCount = 0,
  isStartingCountLoading = true,
  showDocumentCount,
  setShowDocumentCount,
  selectedTreeItems = [],
  assignmentData,
  onAssignmentDataChange,
}) => {
  const [assignFieldValue, setAssignFieldValue] = useState<string>('');
  const [assignFieldErrorMessage, setAssignFieldErrorMessage] =
    useState<string>('');
  const [treeKey, setTreeKey] = useState<number>(0);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  useEffect(() => {
    if (!treeData || treeData.length === 0) return;

    const allIds = getAllIds(treeData);
    setExpandedIds(allIds);
    setTreeKey((prev) => prev + 1); // remount to apply expandedIds
  }, [treeData, showDocumentCount, actionType]);

  const dropdownItems: ISelectItem[] = useMemo(
    () => [
      {
        key: BulkAssignSamplingOption.Count,
        id: BulkAssignSamplingOption.Count,
        name: t(`${enBulkAssign}.samplingOptions.selectedDocumentsCount`),
      },
      {
        key: BulkAssignSamplingOption.Percent,
        id: BulkAssignSamplingOption.Percent,
        name: t(`${enBulkAssign}.samplingOptions.selectedDocumentsPercent`),
      },
      {
        key: BulkAssignSamplingOption.ParentLevelDocsOnly,
        id: BulkAssignSamplingOption.ParentLevelDocsOnly,
        name: t(`${enBulkAssign}.samplingOptions.parenLevelDocs`),
      },
      {
        key: BulkAssignSamplingOption.InclusiveEmail,
        id: BulkAssignSamplingOption.InclusiveEmail,
        name: t(`${enBulkAssign}.samplingOptions.inclusiveEmail`),
      },
    ],
    []
  );

  const treeSelectionChangeHandler = (selectedItems: string[] | null) => {
    onTreeSelect?.(selectedItems);
  };
  // ------------------

  const samplingChangeHandler = (value: ISelectItem | null) => {
    if (
      value &&
      assignmentData &&
      value.key != assignmentData.samplingMethod.key &&
      onAssignmentDataChange
    ) {
      onAssignmentDataChange({
        ...assignmentData,
        samplingMethod: value,
        percentToAssign: '',
        numberToAssign: '',
      });
    }
  };
  // ------------------

  const assignFieldChangeHandler = (value: string) => {
    const newValue = value
      .trim()
      .replace(/^0+(?!$)/, '') // allow only digit
      .replace(/\D/g, ''); // prevent floating point
    if (!Number.isNaN(Number(value)) || newValue == '') {
      setAssignFieldValue(newValue);
      if (
        assignmentData &&
        assignmentData.samplingMethod.key === BulkAssignSamplingOption.Count &&
        onAssignmentDataChange
      ) {
        onAssignmentDataChange({...assignmentData, numberToAssign: newValue});
      } else if (
        assignmentData &&
        assignmentData.samplingMethod.key ===
          BulkAssignSamplingOption.Percent &&
        onAssignmentDataChange
      ) {
        onAssignmentDataChange({
          ...assignmentData,
          percentToAssign: newValue,
        });
      }
    }
  };
  // ------------------

  const persistContentHitsCheckboxHandler = (value: boolean) => {
    if (assignmentData && onAssignmentDataChange) {
      onAssignmentDataChange({...assignmentData, persistContentHits: value});
    }
  };
  // ------------------

  const showDocCountToggleHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const val = event.target.checked ? 1 : 0;
    setShowDocumentCount?.(val);
  };

  const isAssignmentValid = (
    assignData: AssignState | null,
    selectedTreeItems: string[]
  ) => {
    if (
      actionType == BulkAssignActionOption.UnassignDoc &&
      selectedTreeItems.length > 0
    ) {
      return true;
    }
    let isAssignFieldValid = true;
    const isTreeItemSelected = selectedTreeItems.length > 0;
    if (assignData?.samplingMethod.key == BulkAssignSamplingOption.Count) {
      if (
        +assignData.numberToAssign > startingCount ||
        +assignData.numberToAssign < 1
      ) {
        isAssignFieldValid = false;
        setAssignFieldErrorMessage(t(`${enBulkAssign}.countErrorMessage`));
      } else {
        setAssignFieldErrorMessage('');
      }
    } else if (
      assignData?.samplingMethod.key == BulkAssignSamplingOption.Percent
    ) {
      if (
        +assignData.percentToAssign > TOTAL_PERCENT ||
        +assignData.percentToAssign < 1
      ) {
        isAssignFieldValid = false;
        setAssignFieldErrorMessage(t(`${enBulkAssign}.percentErrorMessage`));
      } else {
        setAssignFieldErrorMessage('');
      }
    }
    return isAssignFieldValid && isTreeItemSelected;
  };

  // Trigger validaton
  useEffect(() => {
    if (onError && !isStartingCountLoading) {
      if (isAssignmentValid(assignmentData ?? null, selectedTreeItems)) {
        onError('');
      } else {
        onError('has error');
      }
    }
  }, [actionType, isStartingCountLoading, selectedTreeItems, assignmentData]);

  useEffect(() => {
    if (assignmentData) {
      setAssignFieldValue(
        assignmentData.samplingMethod.key == BulkAssignSamplingOption.Count
          ? assignmentData.numberToAssign
          : assignmentData.percentToAssign
      );
    }
  }, [assignmentData]);

  return (
    <Box className="assignment-panel" data-testid="assignment-panel">
      <Box className="panel-heading-box" data-testid="panel-heading-box">
        <Typography
          className="typo-text subhead4_size"
          data-testid="assignment-panel-title"
          tabIndex={0}
        >
          {assignmentType === 'existing'
            ? t(`${enBulkAssign}.selectExistingAssignments`)
            : t(`${enBulkAssign}.selectNewAssignment`)}
        </Typography>
      </Box>
      <Box
        className="panel-content-box"
        data-testid="panel-content-box"
        tabIndex={0}
      >
        {/* Assignment Tree */}
        <Box
          className={`assignment-tree-box ${assignmentType === 'existing' ? 'existing-tree' : 'new-tree'}`}
          data-testid="assignment-tree-box"
        >
          {!treeData.length || !expandedIds.length ? (
            <ArrLoadingSpinner />
          ) : actionType === BulkAssignActionOption.AssignDoc ? (
            <ArrTreeView
              key={treeKey}
              itemsList={treeData}
              handleNoResultMessage={t(`${enBulkAssign}.noResult`)}
              allowGlobalSelect={true}
              treeSelectedItems={
                assignmentType === 'existing' ? selectedTreeItems : null
              }
              selectedItem={
                assignmentType === 'new' ? selectedTreeItems[0] : ''
              }
              multipleSelection={assignmentType === 'existing'}
              isShowCountLabel={false}
              isShowCheckbox={assignmentType === 'existing'}
              showActions={false}
              customContentHeight={TREE_CONTENT_HEIGHT}
              setSelectedItem={treeSelectionChangeHandler}
              data-testid="assignment-tree"
              defaultExpandedIds={expandedIds}
              setExpandedFolders={setExpandedIds}
            />
          ) : (
            <ArrTreeView
              key={treeKey}
              itemsList={unAssignTreeData || []}
              handleNoResultMessage={t(`${enBulkAssign}.noResult`)}
              allowGlobalSelect={true}
              treeSelectedItems={selectedTreeItems}
              multipleSelection={assignmentType === 'existing'}
              isShowCountLabel={false}
              isShowCheckbox={assignmentType === 'existing'}
              showActions={false}
              customContentHeight={TREE_CONTENT_HEIGHT}
              setSelectedItem={treeSelectionChangeHandler}
              data-testid="assignment-tree"
              defaultExpandedIds={expandedIds}
              setExpandedFolders={setExpandedIds}
            />
          )}
        </Box>

        {/* Assignment Options */}
        <Box className="assignment-options" data-testid="assignment-content">
          <Box
            className="option"
            data-testid="option-starting-count"
            tabIndex={0}
          >
            <Typography
              className="typo-text body3_size"
              data-testid="starting-count-title"
            >
              {actionType === BulkAssignActionOption.AssignDoc
                ? t(`${enBulkAssign}.startingCountDocs`)
                : t(`${enBulkAssign}.countOfDocs`)}
            </Typography>
            <Typography
              className="typo-text body3_size"
              data-testid="starting-count"
            >
              {isStartingCountLoading ? (
                <ArrLoadingSpinner className="arr-loading-small" />
              ) : (
                startingCount
              )}
            </Typography>
          </Box>
          {actionType === BulkAssignActionOption.UnassignDoc && (
            <Box
              className="tooltip-label"
              data-testid="show-doc-count-tooltip-box"
              tabIndex={0}
            >
              <Typography
                className="typo-text body3_size"
                data-testid="show-doc-count-tooltip-text"
              >
                {t(`${enBulkAssign}.samplingOptions.showDocumentCounts`)}
              </Typography>
              <ArrToggle
                checked={showDocumentCount === 1}
                onChange={showDocCountToggleHandler}
                dataTestId="show-document-count-toggle"
              />
            </Box>
          )}
          {actionType === BulkAssignActionOption.AssignDoc && (
            <Box className="option" data-testid="option-sampling-method">
              <Box
                className="tooltip-label"
                data-testid="sampling-method-tooltip-box"
                tabIndex={0}
              >
                <Typography
                  className="typo-text body3_size"
                  data-testid="sampling-method-tooltip-text"
                >
                  {t(`${enBulkAssign}.samplingMethod`)}
                </Typography>
                <Box display="flex">
                  <ArrTooltipWrapper
                    title={t(`${enBulkAssign}.samplingTooltipTitle`)}
                    data-testid="tooltip-title-wrapper"
                    trigger={
                      <IconButton
                        aria-label="Info"
                        className="tooltip-button"
                        data-testid="tooltip-title-button-sampling"
                      >
                        <InfoOutlinedIcon
                          className="tooltip-icon"
                          fontSize="inherit"
                          data-testid="tooltip-title-icon"
                        />
                      </IconButton>
                    }
                  >
                    <Trans
                      i18nKey={`${enBulkAssign}.samplingTooltipContent`}
                      components={{
                        ul: <ul data-testid="assignment-panel-ul" />,
                        li: <li data-testid={`assignment-panel-li`} />,
                        strong: (
                          <strong data-testid="assignment-panel-strong" />
                        ),
                      }}
                    />
                  </ArrTooltipWrapper>
                  <Typography
                    component="span"
                    className="typo-text body3_size"
                    data-testid="assignment-panel-colon-separator"
                  >
                    {':'}
                  </Typography>
                </Box>
              </Box>
              <ArrDropdown
                dataTestId="sampling-dropdown"
                sizeVariant="medium"
                dropdownLabel={t(
                  `${enBulkAssign}.samplingOptions.selectedDocumentsCount`
                )}
                // selectedValues={sampling}
                selectedValues={assignmentData?.samplingMethod ?? null}
                dropdownItems={dropdownItems}
                onSelectionChange={samplingChangeHandler}
                data-testid="sampling-method-dropdown"
              />
            </Box>
          )}
          {/* {actionType === 'assign_documents' && sampling.key === 'count' && ( */}
          {actionType === BulkAssignActionOption.AssignDoc &&
            assignmentData?.samplingMethod.key ===
              BulkAssignSamplingOption.Count && (
              <Box className="option" data-testid="option-number" tabIndex={0}>
                <Typography
                  className="typo-text body3_size"
                  data-testid="number-to-assign-text"
                >
                  {t(`${enBulkAssign}.numberToAssign`)}
                </Typography>
                <ArrTextField
                  className="option-number"
                  state={assignFieldErrorMessage ? 'error' : 'enabled'}
                  sizeVariant="medium"
                  value={assignFieldValue}
                  onChange={(e) => assignFieldChangeHandler(e.target.value)}
                  supportText={assignFieldErrorMessage}
                  showErrorIcon
                  dataTestId="assignment-panel-number-to-assign"
                />
              </Box>
            )}

          {/* {actionType === 'assign_documents' && sampling.key === 'percent' && ( */}
          {actionType === BulkAssignActionOption.AssignDoc &&
            assignmentData?.samplingMethod.key ===
              BulkAssignSamplingOption.Percent && (
              <Box className="option" data-testid="percent-number" tabIndex={0}>
                <Typography
                  className="typo-text body3_size"
                  data-testid="percent-to-assign-text"
                >
                  {t(`${enBulkAssign}.percentToAssign`)}
                </Typography>
                <ArrTextField
                  className="option-number"
                  state={assignFieldErrorMessage ? 'error' : 'enabled'}
                  sizeVariant="medium"
                  value={assignFieldValue}
                  onChange={(e) => assignFieldChangeHandler(e.target.value)}
                  supportText={assignFieldErrorMessage}
                  showErrorIcon
                  dataTestId="assignment-panel-percent-to-assign"
                />
              </Box>
            )}

          {actionType === BulkAssignActionOption.AssignDoc && (
            <Box className="option" data-testid="option-persist-content">
              <Box
                className="tooltip-label"
                data-testid="persist-content-tooltip-box"
                tabIndex={0}
              >
                <Typography
                  className="typo-text body3_size"
                  data-testid="persist-content-tooltip-text"
                >
                  {t(`${enBulkAssign}.persistContent`)}
                </Typography>
                <Box display="flex">
                  <ArrTooltipWrapper
                    data-testid="tooltip-title-wrapper"
                    trigger={
                      <IconButton
                        aria-label="Info"
                        className="tooltip-button"
                        data-testid="tooltip-title-button"
                      >
                        <InfoOutlinedIcon
                          className="tooltip-icon"
                          fontSize="inherit"
                          data-testid="tooltip-title-icon"
                        />
                      </IconButton>
                    }
                  >
                    {t(`${enBulkAssign}.persistContentTooltipContent`)}
                  </ArrTooltipWrapper>
                  <Typography
                    component="span"
                    className="typo-text body3_size"
                    data-testid="assignment-panel-separator-text"
                  >
                    {':'}
                  </Typography>
                </Box>

                <ArrCheckBox
                  variant="primary"
                  size="large"
                  onChange={persistContentHitsCheckboxHandler}
                  checked={assignmentData?.persistContentHits || false}
                  data-testid="persist-content-checkbox"
                  aria-label="persist-content-checkbox"
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export {AssignmentPanel};
export default AssignmentPanel;
